import { useState, type FormEvent } from "react";
import { de } from "@ainkauf/core";
import { getSupabase } from "@/lib/supabase";
import { signOut } from "@/lib/auth";

/**
 * Nach der Anmeldung, solange der Nutzer in keiner Familie ist: anlegen oder
 * beitreten. Beides läuft über eine RPC, die alles in einer Transaktion
 * erledigt, damit nie eine Familie ohne Eigentümer entsteht.
 */
export function FamilyGateScreen({ onDone }: { onDone: () => void }) {
  const [mode, setMode] = useState<"anlegen" | "beitreten">("anlegen");
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const input = value.trim();
    if (input.length === 0) return;

    const client = getSupabase();
    if (!client) return;

    setBusy(true);
    setError(null);
    const { error: rpcError } =
      mode === "anlegen"
        ? await client.rpc("create_family", { family_name: input })
        : await client.rpc("join_family", { invite_code: input });

    setBusy(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    onDone();
  }

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 px-5">
      <h1 className="text-[2.5rem]">{mode === "anlegen" ? de.family.create : de.family.join}</h1>

      <div className="flex gap-2" role="tablist">
        {(["anlegen", "beitreten"] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={mode === option}
            onClick={() => {
              setMode(option);
              setValue("");
              setError(null);
            }}
            className={`min-h-[48px] flex-1 rounded-full px-4 font-semibold transition-colors ${
              mode === option ? "bg-accent text-white" : "bg-card text-ink-2 shadow-[var(--shadow-card)]"
            }`}
          >
            {option === "anlegen" ? de.family.create : de.family.join}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="flex flex-col gap-3">
        <label className="flex flex-col gap-1.5 text-sm text-ink-2">
          {mode === "anlegen" ? de.family.nameLabel : de.family.codeLabel}
          <input
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
            }}
            autoComplete="off"
            autoCapitalize={mode === "beitreten" ? "characters" : "words"}
            data-testid="family-input"
            className="karte min-h-[56px] px-4 text-ink outline-none"
          />
        </label>
        {mode === "beitreten" && <p className="text-sm text-ink-2">{de.family.codeHint}</p>}

        <button
          type="submit"
          disabled={busy}
          data-testid="family-submit"
          className="knopf-haupt min-h-[56px] px-4 text-[17px] disabled:opacity-60"
        >
          {busy ? de.common.loading : de.common.save}
        </button>
        {error !== null && (
          <p role="alert" className="text-sm text-accent">
            {error}
          </p>
        )}
      </form>

      <button type="button" onClick={() => void signOut()} className="min-h-[44px] text-sm text-ink-2 underline underline-offset-4">
        {de.auth.signOut}
      </button>
    </div>
  );
}
